import { inventoryQcList, inventoryQcState, qcList, QcListBody } from "@/apis/inventoryv2";
import useDataList from "@/hooks/useDataList";
import usePage from "@/layout/hooks/usePage";
import STableLayout, { DEFAULT_PAGE_SIZE, TableLayoutRef } from "@/styles/STableLayout";
import SText from "@/styles/SText";
import { CommonListParams } from "@/types/common";
import { IQCOrder, OrderV2, QCStateEnum, QCTypeEnum } from "@/types/inventoryV2";
import { Button, Dropdown, Flex, Input, notification, Select, TableProps, Typography } from "antd";
import { AnyObject } from "antd/es/_util/type";
import { useEffect, useImperativeHandle, useRef, useState, forwardRef, FC, RefObject } from "react";
import { useTranslation } from "react-i18next";
import useIqcEnums from "./hooks/useIqcEnums";
import WMSAvatar from "@/styles/WMSAvatar";
import { getUserAvatarTip, getUserName } from "@/utils/user";
import { dateFormat } from "@/utils/format";
import dayjs from "dayjs";
import { PageId } from "@/types/page";
import IQCDetail from "./components/IQCDetail";
import OrderDetailV2 from "../Order/components/OrderDetailV2";
import AddIQC from "./components/AddIQC";
import { useSelector } from "react-redux";
import { RootState } from "@/store";
import { SConModal } from "@/styles/SConModal";
import ListFilterBar from "@/components/ListFilterBar";
import useCommittedFilters from "@/components/ListFilterBar/hooks/useCommittedFilters";

export type IQCListRef = {
  addItem: (item: IQCOrder) => void;
};
interface IQCListProps {
  listRef?: RefObject<IQCListRef>;
  inventoryOrder?: OrderV2;
  handleNext?: () => void;
  handleQCState?: (val: QCStateEnum, orderId: string) => void;
}

interface IQCFilters {
  keyword: string;
  qcType: QCTypeEnum | undefined;
  qcState: QCStateEnum | undefined;
}

const INITIAL_IQC_FILTERS: IQCFilters = {
  keyword: '',
  qcType: undefined,
  qcState: undefined,
};

const IQCList: FC<IQCListProps> = ({ listRef, inventoryOrder, handleNext, handleQCState }) => {
  const { t } = useTranslation();
  const { pageId } = usePage();
  const [list, total, loading, { fetchList, updateItem, addItem }] = useDataList<IQCOrder, [CommonListParams<QcListBody>, string]>({ fetchApi: !!!inventoryOrder?.id ? qcList : inventoryQcList });
  const { QCTypeEnumList, QCStateEnumList, getQCType, getQCState } = useIqcEnums();
  const { curUser } = useSelector((state: RootState) => state.userReducer)

  const tableRef = useRef<TableLayoutRef | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const [openDetail, setOpenDetail] = useState<IQCOrder | null>(null);
  const [openOrder, setOpenOrder] = useState<OrderV2 | null>(null);
  const [addOpen, setAddOpen] = useState<boolean>(false);

  const [orderQcState, setOrderQcState] = useState<QCStateEnum | null>(inventoryOrder?.iqcState ?? null);
  const [orderQcLoaing, setOrderQcLoaing] = useState<boolean>(false);
  const {
    draftFilters,
    appliedFilters,
    updateDraftFilters,
    commitFilters,
    replaceFilters,
  } = useCommittedFilters<IQCFilters>(INITIAL_IQC_FILTERS);
  const { keyword, qcType, qcState } = draftFilters;

  useImperativeHandle(listRef, () => ({ addItem }))

  useEffect(() => {
    const nextFilters = replaceFilters(INITIAL_IQC_FILTERS);
    fetch(0, nextFilters);
    return () => abortControllerRef.current?.abort();
    // 首次加载只执行一次，筛选提交由事件显式触发。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [replaceFilters]);

  const fetch = (offset: number, filters: IQCFilters) => {
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;
    fetchList({
      body: {
        keyword: filters.keyword,
        qcStates: filters.qcState !== undefined ? [filters.qcState] : undefined,
        qcTypes: filters.qcType !== undefined ? [filters.qcType] : undefined
      },
      pagination: {
        offset,
        take: tableRef?.current?.pageSize || DEFAULT_PAGE_SIZE,
      },
      controller: abortControllerRef.current
    }, inventoryOrder?.id ?? '')
  };

  const handleSearch = () => {
    const nextFilters = commitFilters();
    tableRef.current?.resetTablePage();
    fetch(0, nextFilters);
  };

  const handleClearFilters = () => {
    const nextFilters = replaceFilters(INITIAL_IQC_FILTERS);
    tableRef.current?.resetTablePage();
    fetch(0, nextFilters);
  };

  const hanndleAddQC = () => {
    if (addOpen) return;
    if (inventoryOrder?.id && handleNext) handleNext()
    else setAddOpen(true);
  };

  const handleClose = (type: QCStateEnum | "CANCEL") => {
    if (type === "CANCEL") setOpenDetail(null)
    else {
      openDetail && updateItem({
        ...openDetail,
        qcState: type,
        inspectedAt: Date.now(),
        inspector: curUser
      })
      setOpenDetail(null);
    }
  };
  const handleAddClose = (order: IQCOrder | null) => {
    setAddOpen(false);
    if (order) addItem({ ...order })
  };

  const changeQc = (state: QCStateEnum.QC_FAIL | QCStateEnum.QC_PASS | QCStateEnum.QC_ING) => {
    if (loading) return;
    setOrderQcLoaing(true)
    let resInfo = getQCState(state)
    inventoryQcState(inventoryOrder!.id, state)
      .then(() => {
        notification.success({
          message: t('warehouse.optSucc'),
          description: state === QCStateEnum.QC_ING ? t('warehouse.canAddQc') : t('warehouse.orderQcDone', { number: inventoryOrder!.number, res: resInfo!.label })
        });
        setOrderQcState(state);
        handleQCState?.(state, inventoryOrder!.id);
      })
      .catch(() => notification.error({ message: ('warehouse.optFail'), }))
      .finally(() => setOrderQcLoaing(false))
  }

  const columns: TableProps<IQCOrder>['columns'] = [
    {
      title: <SText text={t('warehouse.iqcNumber')} />,
      key: "number",
      render: (_, record) => <Typography.Link onClick={() => setOpenDetail(record)}>{record.number}</Typography.Link>
    },
    {
      title: <SText text={t('warehouse.iqcInventory')} />,
      key: "sourceOrder",
      render: (_, record) => <Typography.Link onClick={() => setOpenOrder(record.sourceOrder || inventoryOrder || null)}>{record.sourceOrder?.number ?? inventoryOrder?.number ?? ''}</Typography.Link>
    },
    {
      title: <SText text={t('warehouse.iqcType')} />,
      key: "iqcType",
      render: (_, record) => {
        let info = getQCType(record.qcType);
        return info && <SText tagMode color={info.color} text={info.label} />
      }
    },
    {
      title: <SText text={t('warehouse.iqcState')} />,
      key: "iqcState",
      render: (_, record) => {
        let info = getQCState(record.qcState);
        return info && <SText tagMode color={info.color} text={info.label} />
      }
    },
    {
      title: <SText text={t("common.creator")} />,
      dataIndex: "creator",
      width: 100,
      render: (_, item) => {
        let userData = item.creator;
        return (
          userData && <Flex>
            <div>
              <WMSAvatar creator={userData} uniChar={userData.id}
                avatarId={userData.avatar}
                size={24} alt={getUserAvatarTip(userData)}
              />
            </div>
            <SText text={getUserName(userData)} />
          </Flex>
        )
      },
    },
    {
      title: <SText text={t('warehouse.iqcUser')} />,
      key: "inspector",
      hidden: true,
      render: (_, item) => {
        let userData = item.inspector;
        return (
          userData && <Flex>
            <div>
              <WMSAvatar creator={userData} uniChar={userData.id}
                avatarId={userData.avatar}
                size={24} alt={getUserAvatarTip(userData)}
              />
            </div>
            <SText text={getUserName(userData)} />
          </Flex>
        )
      },
    },
    {
      title: <SText text={t('warehouse.iqcTime')} />,
      key: "inspectedAt",
      render: (_, record) => record.inspectedAt && dateFormat(dayjs(record.inspectedAt), true)
    }
  ];

  const headerChildren = (
    <Flex justify='space-between' align="center" gap={8} style={{ width: "100%" }} >
      <Button type="primary" onClick={hanndleAddQC} disabled={!!orderQcState && [QCStateEnum.QC_PASS, QCStateEnum.QC_FAIL].includes(orderQcState)} >{t('warehouse.addQc')}</Button>
      {
        !!inventoryOrder && (orderQcState === QCStateEnum.QC_FAIL || orderQcState === QCStateEnum.QC_PASS) && (
          <SConModal disable={orderQcLoaing} title={t('common.tip')} content={t('warehouse.reQcTip')} onOk={() => Promise.resolve(changeQc(QCStateEnum.QC_ING))}>
            <Button loading={orderQcLoaing}>{t('warehouse.reQc')}</Button>
          </SConModal>
        )
      }
      {
        !!inventoryOrder && (orderQcState !== QCStateEnum.QC_FAIL && orderQcState !== QCStateEnum.QC_PASS) && <Dropdown trigger={["click"]}
          menu={{
            items: [
              {
                key: 'NORM',
                label: (
                  <SConModal disable={orderQcLoaing} title={t('common.tip')} content={t('warehouse.passQcTip')} onOk={() => Promise.resolve(changeQc(QCStateEnum.QC_PASS))}>
                    <Button type="link" style={{ color: "unset" }} loading={orderQcLoaing}>{t('warehouse.passQc')}</Button>
                  </SConModal>
                )
              },
              {
                key: 'WARE',
                label: (
                  <SConModal disable={orderQcLoaing} title={t('common.tip')} content={t('warehouse.failQcTip')} onOk={() => Promise.resolve(changeQc(QCStateEnum.QC_FAIL))}>
                    <Button type="link" style={{ color: "unset" }} loading={orderQcLoaing}>{t('warehouse.failQc')}</Button>
                  </SConModal>
                )
              },
            ],
          }}
        >
          <Button loading={orderQcLoaing} disabled={total === 0} >{t('warehouse.doneQC')}</Button>
        </Dropdown>
      }
      <div style={{ flex: 1, minWidth: 0 }}>
        <ListFilterBar
          onSearch={handleSearch}
          onReset={handleClearFilters}
          disabled={loading}
        >
          {({ submit }) => (
            <>
              {!!!inventoryOrder && <>
                <Select
                  options={QCTypeEnumList}
                  value={qcType}
                  onChange={value => updateDraftFilters({ qcType: value })}
                  style={{ minWidth: 120 }}
                  placeholder={t('warehouse.typePl')}
                  allowClear
                />
                <Select
                  options={QCStateEnumList}
                  value={qcState}
                  onChange={value => updateDraftFilters({ qcState: value })}
                  style={{ minWidth: 120 }}
                  placeholder={t('warehouse.statePl')}
                  allowClear
                />
              </>}
              <Input
                style={{ width: 200 }}
                value={keyword}
                onChange={event => updateDraftFilters({ keyword: event.target.value })}
                placeholder={t('category.plSearch')}
                onPressEnter={submit}
              />
            </>
          )}
        </ListFilterBar>
      </div>
    </Flex>
  )

  return (
    <Flex vertical style={{ width: "100%", height: "100%" }}>
      <STableLayout
        columns={columns as Array<AnyObject>}
        dataSource={list}
        total={total}
        headerChildren={headerChildren}
        loading={loading}
        tableLayoutRef={tableRef}
        handleChange={offset => fetch(offset, appliedFilters)}
      />
      {openDetail && <IQCDetail detail={openDetail} onClose={handleClose} />}
      {openOrder && <OrderDetailV2 id={openOrder.id} handleCancel={() => setOpenOrder(null)} handleRm={() => { }} />}
      {addOpen && !!!inventoryOrder && <AddIQC open={addOpen} onClose={handleAddClose} />}
    </Flex >
  )
};

export default IQCList;
